import type { ToastContext } from "@expert/common-ui";
import { useGlobalNotification } from "@expert/common-ui";
import { getLogger } from "@expert/logging";
import { useExpertWorkspaceWebSocket } from "@expert/websocket";
import pRetry from "p-retry";
import { useEffect, useState } from "react";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { sdkEventBus, useAgentSdk } from "@/sdk";

const logger = getLogger({ module: "EvlConnectionNotifications" });
let isReconnected = true;

export default function EvlConnectionNotifications() {
    const sdk = useAgentSdk();
    const { technicalIssues } = usePartnerConfig();
    const globalNotification = useGlobalNotification();
    const [evlDisconnectedNotification, setEvlDisconnectedNotification] = useState<ToastContext>();
    const webSocket = useExpertWorkspaceWebSocket();

    useEffect(() => {
        if (!technicalIssues?.autoSet) return;

        const evlDisconnectedUnsub = sdkEventBus.on("evl_disconnected", async (_payload) => {
            // We will try sending this request 3 times to get EVL to respond
            // If there is no response we will pop the error notification and set to TechnicalIssues

            try {
                isReconnected = false;

                await pRetry(
                    () => {
                        if (!isReconnected) {
                            webSocket.sendMessageToTenant("ExpertVoiceLink", "request-uuid", {});
                            throw new Error("Retrying evl connection");
                        }
                    },
                    {
                        retries: 3,
                        minTimeout: 2 * 1000,
                        maxTimeout: 10 * 1000,
                    },
                );
            } catch (error: unknown) {
                logger.error({ error }, "Unable to send Evl handshake after multiple attempts");
            }
            if (!isReconnected) {
                logger.info("EVL was disconnected, notifying the expert so they can refresh or reconnect to EVL.");
                if (evlDisconnectedNotification) {
                    globalNotification.clearNotification(evlDisconnectedNotification);
                }
                setEvlDisconnectedNotification(
                    globalNotification.error("EVL has disconnected. Please go to EVL and refresh to reconnect.", {
                        persistent: true,
                    }),
                );

                if (sdk.getAgentActivity() === sdk.availableAgentActivity) {
                    void sdk.setAgentActivity(technicalIssues.agentActivity);
                }
                isReconnected = true;
            }
        });

        const evlReconnectedUnsub = sdkEventBus.on("evl_reconnected", (_payload) => {
            logger.info("EVL was re-connected.");
            isReconnected = true;
            if (evlDisconnectedNotification) {
                globalNotification.clearNotification(evlDisconnectedNotification);
                setEvlDisconnectedNotification(undefined);
            }
            globalNotification.success("Your audio has been re-connected. Please set yourself back to Available.", {
                delay: 60_000,
            });
        });

        return () => {
            evlDisconnectedUnsub();
            evlReconnectedUnsub();
        };
    }, [
        evlDisconnectedNotification,
        globalNotification,
        sdk,
        technicalIssues?.agentActivity,
        technicalIssues?.autoSet,
        webSocket,
    ]);

    return null;
}
